import React, { useEffect, useState, memo } from "react";
import { baseURL } from "../../include/api";
import { CircleX } from "lucide-react";

const VariantSelectionModal = memo(function VariantSelectionModal({
  isOpen,
  onClose,
  product,
  customer,
  onConfirmAdd,
  loading,
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const isB2C = !customer || customer.customerType === "individual";

  // ✅ FIXED: Set default option with LOWEST minQty for qtyAndPrice
  useEffect(() => {
    if (isOpen && product) {
      if (product?.qtyAndPrice?.length > 0) {
        // Find option with MINIMUM minQty
        const lowestQtyOption = product.qtyAndPrice.reduce((minOpt, currentOpt) => {
          const minQtyCurrent = currentOpt.minQty || 999999;
          const minQtyMin = minOpt.minQty || 999999;
          return minQtyCurrent < minQtyMin ? currentOpt : minOpt;
        }, product.qtyAndPrice[0]);
        
        setSelectedOption({
          type: "qtyAndPrice",
          value: lowestQtyOption,
        });
      } else if (product?.productVariant?.length > 0) {
        // For variants, select first one (or add similar logic if needed)
        setSelectedOption({
          type: "productVariant",
          value: product.productVariant[0],
        });
      }
    } else {
      setSelectedOption(null);
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const handleConfirm = () => {
    if (selectedOption) {
      onConfirmAdd(product, selectedOption);
    }
  };

  // ✅ FIXED: Calculate price with all 3 logics
  const calculateVariantPrice = (option, type) => {
    // --- LOGIC 2: qtyAndPrice - Direct selling price (NO discount calculation) ---
    if (type === "qtyAndPrice") {
      const price = isB2C
        ? parseFloat(option.setB2cPrice)
        : parseFloat(option.setB2bPrice);
      return isNaN(price) ? 0 : price;
    } 
    // --- LOGIC 3: productVariant - Retail Price - Discount = Selling Price ---
    else if (type === "productVariant") {
      const basePrice = parseFloat(option.price) || 0; // Retail Price
      const discMode = isB2C
        ? (option.b2cDiscountMode || "").toLowerCase()
        : (option.b2bDiscountMode || "").toLowerCase();
      const discValue = isB2C
        ? parseFloat(option.b2cDiscountValue) || 0
        : parseFloat(option.b2bDiscountValue) || 0;

      let finalPrice = basePrice;
      if (discMode === "percent") {
        finalPrice = basePrice - (basePrice * discValue) / 100;
      } else if (discMode === "amount") {
        finalPrice = basePrice - discValue;
      }
      return Math.max(0, finalPrice);
    }
    return 0;
  };

  // ✅ FIXED: Display option label with correct price
  const getOptionLabel = (option, type) => {
    if (type === "qtyAndPrice") {
      const minQty = option.minQty || 0;
      const maxQty = option.maxQty || 0;
      const basedOn = option.basedOn || "qty";
      const price = calculateVariantPrice(option, type);
      
      // Display: "50 - 99 bags - ₹200.00"
      return `${minQty}${maxQty ? ` - ${maxQty}` : ""} ${basedOn} - ₹${price.toFixed(2)}`;
    } else if (type === "productVariant") {
      const variantName =
        option.variantName || option.name || option.title || `Variant ${option.id || ""}`;
      const price = calculateVariantPrice(option, type);
      
      // Display: "06 mm - ₹70.00"
      return `${variantName} - ₹${price.toFixed(2)}`;
    }
    return "";
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          width: "100%",
          maxWidth: "600px",
          borderRadius: "12px",
          padding: "32px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          animation: "modalFadeIn 0.2s ease-out",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex-between mb-16"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h5 className="fw-semibold text-lg" style={{ fontSize: "1.25rem", fontWeight: "600" }}>
            Select Option
          </h5>
          <span onClick={onClose} className="d-flex justify-content-end">
            <CircleX color="#ddddddb0" />
          </span>
        </div>

        {/* Product Info */}
        <div
          className="mb-20 pb-16 pt-16 border-b border-gray-100"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            marginBottom: "32px",
            paddingBottom: "24px",
            borderBottom: "1px solid #f3f4f6",
            borderRadius: "10px",
          }}
        >
          <img
            src={baseURL + "/" + product.mainImage}
            alt={product.name}
            style={{
              width: "160px",
              height: "160px",
              objectFit: "cover",
              borderRadius: "12px",
              backgroundColor: "#f9fafb",
              marginBottom: "16px",
            }}
          />
          <h6 className="text-md mb-0 fw-bold">{product.name}</h6>
        </div>

        {/* Options List */}
        <div className="mb-20" style={{ marginBottom: "32px" }}>
          <label
            className="text-sm fw-medium text-gray-700 mb-10 block"
            style={{
              display: "block",
              marginBottom: "12px",
              fontWeight: "500",
              color: "#374151",
            }}
          >
            {product.qtyAndPrice?.length > 0
              ? "Select Quantity Range"
              : "Select Variant"}
          </label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              maxHeight: "300px",
              overflowY: "auto",
              paddingRight: "4px",
            }}
          >
            {product.qtyAndPrice?.length > 0
              ? product.qtyAndPrice.map((option, idx) => {
                  const optionPrice = calculateVariantPrice(option, "qtyAndPrice");
                  const isSelected = selectedOption?.value?.id === option.id;
                  
                  return (
                    <label
                      key={option.id || idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "16px",
                        borderRadius: "8px",
                        border: isSelected ? "2px solid #FA6400" : "1px solid #e5e7eb",
                        backgroundColor: isSelected ? "#FEE8D9" : "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="radio"
                        name="qtyOption"
                        style={{
                          accentColor: "#FA6400",
                          width: "16px",
                          height: "16px",
                          flexShrink: 0,
                        }}
                        checked={isSelected}
                        onChange={() =>
                          setSelectedOption({
                            type: "qtyAndPrice",
                            value: option,
                          })
                        }
                      />
                      <span
                        className="text-sm flex-1 text-center"
                        style={{
                          fontSize: "0.875rem",
                          flex: 1,
                          textAlign: "center",
                        }}
                      >
                        {/* Display: "50-99 bags" + price */}
                        <div className="fw-medium">
                          {option.minQty}{option.maxQty ? `-${option.maxQty}` : ""} {option.basedOn}
                        </div>
                        <div className="text-main-600 fw-bold">₹{optionPrice.toFixed(2)}</div>
                      </span>
                    </label>
                  );
                })
              : product.productVariant?.length > 0
              ? product.productVariant.map((option, idx) => {
                  const optionPrice = calculateVariantPrice(option, "productVariant");
                  const isSelected = selectedOption?.value?.id === option.id;
                  
                  return (
                    <label
                      key={option.id || idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "16px",
                        borderRadius: "8px",
                        border: isSelected ? "2px solid #FA6400" : "1px solid #e5e7eb",
                        backgroundColor: isSelected ? "#FEE8D9" : "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="radio"
                        name="variantOption"
                        style={{
                          accentColor: "#FA6400",
                          width: "16px",
                          height: "16px",
                          flexShrink: 0,
                        }}
                        checked={isSelected}
                        onChange={() =>
                          setSelectedOption({
                            type: "productVariant",
                            value: option,
                          })
                        }
                      />
                      <span
                        className="text-sm flex-1 text-center"
                        style={{
                          fontSize: "0.875rem",
                          flex: 1,
                          textAlign: "center",
                        }}
                      >
                        {/* Display: "06 mm" + price */}
                        <div className="fw-medium">
                          {option.variantName || option.title || option.name || `Variant ${option.id}`}
                        </div>
                        <div className="text-main-600 fw-bold">₹{optionPrice.toFixed(2)}</div>
                      </span>
                    </label>
                  );
                })
              : null}
          </div>
        </div>

        {/* Actions */}
        <div
          className="flex gap-12 mt-60"
          style={{
            display: "flex",
            gap: "16px",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline-main btn-sm rounded-5 py-16 fw-normal w-50"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedOption || loading}
            className="btn btn-main btn-sm rounded-5 py-16 fw-normal w-50"
            style={{
              cursor: !selectedOption || loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #fff",
                    borderRightColor: "transparent",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 1s linear infinite",
                  }}
                  role="status"
                />
                Adding...
              </>
            ) : (
              <>
                {`ADD TO CART \u00A0\u00A0\u00A0\u00A0`}
                <i className="ph ph-shopping-cart" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Animation */}
      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

export default VariantSelectionModal;